import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { m as motion, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight,
  X,
  FileText,
  Phone,
  Mail,
  ChevronRight,
} from "lucide-react";

/**
 * StickyInquiryRail
 * — A floating, low-friction inquiry CTA pinned to the bottom-right of long content pages.
 * — Appears after the user has scrolled 30% down the page.
 * — Expands into a small action menu (Submit Inquiry · Email · Phone).
 * — Auto-hidden on /contact (no need to re-direct people to where they already are).
 */
export default function StickyInquiryRail() {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isContact = location.pathname.startsWith("/contact-us");
  const isCartridgeDetail = location.pathname.startsWith('/energetics/cartridges/');
  const isEnergetics = location.pathname === '/energetics' || location.pathname.startsWith('/energetics/');
  // Product detail pages render their own full-width mobile sticky CTA bar
  // (Spec Sheet + Submit RFQ). Hiding the rail on those routes avoids the
  // floating bottom-left button stacking on top of the bottom-edge bar.
  const isProductDetail = /^\/mounts-mechanical\/[^/]+$/.test(location.pathname);

  useEffect(() => {
    if (isContact || isCartridgeDetail) {
      setVisible(false);
      return;
    }
    let ticking = false;
    const update = () => {
      const scrolled = window.scrollY;
      const triggerAt = Math.max(window.innerHeight * 0.5, 480);
      const footer = document.querySelector('[data-testid="site-footer"]');
      const footerVisible = isEnergetics && window.innerWidth < 768 && footer && footer.getBoundingClientRect().top < window.innerHeight;
      setVisible(scrolled > triggerAt && !footerVisible);
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [isContact, isCartridgeDetail, isEnergetics, location.pathname]);

  // Close menu when pathname changes
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  if (isContact || isCartridgeDetail) return null;

  return (
    <div
      className={`fixed ${isEnergetics ? 'right-0' : 'left-4 md:left-6'} bottom-4 md:bottom-6 z-[55] pointer-events-none ${
        isProductDetail ? "hidden lg:block" : ""
      }`}
      data-testid="sticky-inquiry-rail"
    >
      <AnimatePresence>
        {visible && (
          <motion.div
            key="cta"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className={`pointer-events-auto flex flex-col ${isEnergetics ? 'items-end' : 'items-start'} gap-3`}
          >
            {/* Expanded menu */}
            <AnimatePresence>
              {open && (
                <motion.div
                  key="menu"
                  initial={{ opacity: 0, y: 10, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.96 }}
                  transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                  className="bg-white shadow-[0_28px_60px_-20px_rgba(0,0,0,0.35)] border border-line w-[300px] max-w-[calc(100vw-16px)] relative"
                  data-testid="sticky-inquiry-menu"
                >
                  {/* Top accent + label */}
                  <div className="absolute top-0 left-0 w-12 h-[2px] bg-signal" />
                  <div className="px-5 pt-5 pb-4 border-b border-line">
                    <div className="font-mono text-[14px] tracking-[0.32em] uppercase text-signal">
                      — Direct Engagement
                    </div>
                    <div className="mt-2 font-display font-bold text-xl text-navy tracking-tight leading-tight">
                      Connect with Valor
                    </div>
                    <p className="mt-1.5 text-steel text-[14px] leading-[1.5]">
                      The Valor team responds within 24 hours.
                    </p>
                  </div>

                  <ul>
                    {[
                      {
                        to: "/contact-us",
                        Icon: FileText,
                        title: "Send a Message",
                        sub: "RFI · RFQ · Program Brief",
                        testid: "rail-link-inquiry",
                        external: false,
                      },
                      {
                        to: "mailto:info@valortactical.us",
                        Icon: Mail,
                        title: "Email Valor",
                        sub: "info@valortactical.us",
                        testid: "rail-link-email",
                        external: true,
                      },
                      {
                        to: "tel:+19702438750",
                        Icon: Phone,
                        title: "Direct Call",
                        sub: "(970) 243-8750",
                        testid: "rail-link-phone",
                        external: true,
                      },
                    ].map((item) => {
                      const Inner = (
                        <div className="px-5 py-4 flex items-center gap-4 group hover:bg-bone transition-colors">
                          <div className="w-9 h-9 border border-signal/40 bg-signal/5 group-hover:bg-signal group-hover:border-signal flex items-center justify-center transition-colors shrink-0">
                            <item.Icon
                              className="w-4 h-4 text-signal group-hover:text-white transition-colors"
                              strokeWidth={1.8}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-display font-semibold text-navy text-[14px] tracking-tight leading-tight">
                              {item.title}
                            </div>
                            <div className="text-steel text-[11.5px] leading-tight mt-0.5 truncate">
                              {item.sub}
                            </div>
                          </div>
                          <ChevronRight
                            className="w-4 h-4 text-steel group-hover:text-signal transition-colors shrink-0"
                            strokeWidth={1.8}
                          />
                        </div>
                      );
                      return (
                        <li
                          key={item.testid}
                          className="border-b border-line last:border-b-0"
                        >
                          {item.external ? (
                            <a
                              href={item.to}
                              data-testid={item.testid}
                              className="block"
                            >
                              {Inner}
                            </a>
                          ) : (
                            <Link
                              to={item.to}
                              data-testid={item.testid}
                              className="block"
                              onClick={() => setOpen(false)}
                            >
                              {Inner}
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Main button */}
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className={`group relative bg-signal text-white flex items-center shadow-[0_18px_50px_-18px_rgba(230,28,36,0.65)] hover:shadow-[0_22px_60px_-18px_rgba(230,28,36,0.85)] transition-shadow ${isEnergetics ? 'w-9 min-h-[108px] flex-col justify-center gap-2 py-3' : 'pl-5 pr-3.5 py-3 md:pl-6 md:pr-4 md:py-3.5 gap-3'}`}
              aria-label={open ? "Close inquiry menu" : "Open inquiry menu"}
              aria-expanded={open}
              data-testid="sticky-inquiry-toggle"
            >
              {/* Pulse ping */}
              {!open && !isEnergetics && (
                <span className="absolute -top-1 -right-1 flex h-3 w-3 pointer-events-none">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
                </span>
              )}
              <span className={isEnergetics ? 'font-mono text-[12px] uppercase [writing-mode:vertical-rl]' : 'font-mono text-[14px] tracking-[0.32em] uppercase opacity-80'}>
                {open ? "Close" : "Contact"}
              </span>
              <span className={`font-display font-semibold text-[15.5px] tracking-tight hidden ${isEnergetics ? '' : 'md:inline'}`}>
                {open ? "" : "Connect with Valor"}
              </span>
              <div className={`${isEnergetics ? 'w-5 h-5' : 'w-7 h-7 border border-white/50'} flex items-center justify-center group-hover:bg-white group-hover:border-white group-hover:text-signal transition-colors`}>
                {open ? (
                  <X className="w-3.5 h-3.5" strokeWidth={2.2} />
                ) : (
                  <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.2} />
                )}
              </div>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

